import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { apiConfig } from './authConfig';
import { AppShell } from './components/AppShell';
import { IndustriesPage } from './pages/IndustriesPage';
import { SchemaListPage } from './pages/SchemaListPage';
import { SchemaEditorPage } from './pages/SchemaEditorPage';
import { ParticipantsPage } from './pages/ParticipantsPage';
import { GenerateQRPage } from './pages/GenerateQRPage';
import { BatchQRPage } from './pages/BatchQRPage';
import { AuditPage } from './pages/AuditPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { TestDecodePage } from './pages/TestDecodePage';
import { SelfIntakePage } from './pages/SelfIntakePage';

function SignedOutView() {
  const { login } = useAuth();
  return (
    <div className="signin-page">
      <div className="signin-page__card">
        <img src="/logo.png" alt={apiConfig.tenantDisplayName} className="signin-page__logo" />
        <h1>
          {apiConfig.tenantDisplayName} <span className="signin-page__product">QR Platform</span>
        </h1>
        <p>Define what BACR's QR codes capture, generate codes, and manage participants.</p>
        <button className="btn btn--primary signin-page__button" onClick={() => void login()}>
          Sign in
        </button>
        <p className="signin-page__fine-print">
          Restricted to invited accounts. Contact your administrator if you need access.
        </p>
      </div>
    </div>
  );
}

function AuthedApp() {
  const { tokenReady, userInfo, authError } = useAuth();

  if (!tokenReady) {
    return <div className="full-page-loader">Loading…</div>;
  }
  if (authError) {
    return (
      <div className="signin-page">
        <div className="signin-page__card signin-page__card--error">
          <img src="/logo.png" alt={apiConfig.tenantDisplayName} className="signin-page__logo" />
          <h1>Access denied</h1>
          <p>{authError}</p>
        </div>
      </div>
    );
  }
  if (!userInfo) {
    return (
      <div className="signin-page">
        <div className="signin-page__card">
          <img src="/logo.png" alt={apiConfig.tenantDisplayName} className="signin-page__logo" />
          <h1>Loading account…</h1>
        </div>
      </div>
    );
  }

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Navigate to="/industries" replace />} />
        <Route path="/industries" element={<IndustriesPage />} />
        <Route path="/industries/:industryId/schemas" element={<SchemaListPage />} />
        <Route path="/industries/:industryId/schemas/new" element={<SchemaEditorPage />} />
        <Route path="/industries/:industryId/schemas/:schemaId" element={<SchemaEditorPage />} />
        <Route path="/industries/:industryId/schemas/:schemaId/participants" element={<ParticipantsPage />} />
        <Route path="/industries/:industryId/schemas/:schemaId/generate" element={<GenerateQRPage />} />
        <Route path="/industries/:industryId/schemas/:schemaId/batch" element={<BatchQRPage />} />
        <Route path="/industries/:industryId/audit" element={<AuditPage />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="*" element={<Navigate to="/industries" replace />} />
      </Routes>
    </AppShell>
  );
}

/**
 * Routes split into:
 *  - Public routes (rendered regardless of auth state): /test-decode, /intake
 *  - Authed app (gated by MSAL templates): everything else
 *
 * We branch on pathname BEFORE the auth gate so that scanned QR codes and
 * self-intake links land directly on the public renderer without forcing a
 * sign-in flow.
 */
function isPublicPath(pathname: string): boolean {
  return pathname === '/test-decode' || pathname === '/intake';
}

export function App() {
  const location = useLocation();
  if (isPublicPath(location.pathname)) {
    return (
      <Routes>
        <Route path="/test-decode" element={<TestDecodePage />} />
        <Route path="/intake" element={<SelfIntakePage />} />
      </Routes>
    );
  }
  return (
    <>
      <UnauthenticatedTemplate>
        <SignedOutView />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <AuthedApp />
      </AuthenticatedTemplate>
    </>
  );
}
